import React, { useState, useEffect, useMemo } from 'react'
import { useSilenceAudio } from './useSilenceAudio'
import { useCandidateChat } from '../CandidateChatContext'
import { useTranslation } from '~/hooks/useTranslation'
import AudioParticipantCard from '../../shared/AudioParticipantCard'

/**
 * AudioConversation — candidate audio chat layout (CTS-9778 card redesign).
 */
const AudioConversation = ({
  chatMode,
  hasLocalAudio,
  chatManager,
  connectionState,
  selectedCoach,
}) => {
  const {
    getDarkModeClasses,
    isDarkMode,
    userDisplayName,
    userFirstName,
    username,
    avatar,
  } = useCandidateChat()
  const { t } = useTranslation('coach_chat')
  const [audioBlocked, setAudioBlocked] = useState(false)
  const [remoteAudioMuted, setRemoteAudioMuted] = useState(false)

  const localAudioMuted = connectionState?.audioMuted || false
  const localStream = chatManager?.connectionState?.localStream || null
  const remoteStream = chatManager?.connectionState?.remoteStream || null

  const localPerson = useMemo(
    () => ({
      displayName: userDisplayName || username,
      firstName: userFirstName,
      avatar,
    }),
    [avatar, userDisplayName, userFirstName, username],
  )

  useSilenceAudio({
    chatStatus: connectionState?.chatStatus,
    chatMode: chatMode,
  })

  useEffect(() => {
    const handleAutoplayBlocked = () => setAudioBlocked(true)

    if (chatManager?.rtcService) {
      chatManager.rtcService.addEventListener(
        'autoplayBlocked',
        handleAutoplayBlocked,
      )
    }

    return () => {
      if (chatManager?.rtcService) {
        chatManager.rtcService.removeEventListener(
          'autoplayBlocked',
          handleAutoplayBlocked,
        )
      }
    }
  }, [chatManager])

  // Show mute badge on the coach card when the coach mutes
  useEffect(() => {
    const handlePeerMessage = (data) => {
      if (
        data.messageType === 'audio_muted' ||
        data.messageType === 'audio_unmuted'
      ) {
        setRemoteAudioMuted(data.messageType === 'audio_muted')
      }
    }

    if (chatManager) {
      chatManager.addEventListener('peerMessage', handlePeerMessage)
    }

    return () => {
      if (chatManager) {
        chatManager.removeEventListener('peerMessage', handlePeerMessage)
      }
    }
  }, [chatManager])

  useEffect(() => {
    if (remoteStream) {
      const videoElement = document.getElementById('callerVideo')
      if (videoElement) {
        videoElement.volume = 1.0
        videoElement.play().catch(() => setAudioBlocked(true))
      }
    }
  }, [remoteStream])

  const handleEnableAudio = () => {
    const videoElement = document.getElementById('callerVideo')
    if (videoElement) {
      return videoElement
        .play()
        .then(() => {
          setAudioBlocked(false)
          return true
        })
        .catch(() => false)
    }
    return Promise.resolve(false)
  }

  if (chatMode !== 'audio' || !hasLocalAudio) {
    return null
  }

  return (
    <div
      className={`flex min-h-0 flex-1 flex-row items-stretch justify-center gap-3 border-b border-solid p-3 ${getDarkModeClasses(
        'border-gray-300 bg-cod-gray-50',
        'border-gray-600 bg-gray-800',
      )}`}
    >
      {/* Remote coach */}
      <div className="flex w-1/2 min-w-0 flex-col self-stretch">
        <AudioParticipantCard
          role="coach"
          person={selectedCoach}
          audioStream={remoteStream}
          isMuted={remoteAudioMuted}
          isDarkMode={isDarkMode}
          footer={
            audioBlocked ? (
              <button
                type="button"
                onClick={handleEnableAudio}
                className="rounded-md bg-intoo-green-medium-700 px-4 py-2 font-roboto text-sm font-medium text-white hover:bg-intoo-green-medium-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-intoo-green-medium-700"
              >
                {t('audio_conversation.enable_audio', {
                  defaultValue: 'Click to Enable Audio',
                })}
              </button>
            ) : null
          }
        />
      </div>

      {/* Local candidate */}
      <div className="flex w-1/2 min-w-0 flex-col self-stretch">
        <AudioParticipantCard
          role="candidate"
          person={localPerson}
          audioStream={localStream}
          isMuted={localAudioMuted}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>
  )
}

export default AudioConversation
